import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { FiLogOut, FiUser, FiUsers, FiFileText, FiToggleLeft, FiToggleRight, FiHome, FiShield, FiDroplet, FiMapPin, FiActivity } from 'react-icons/fi';
import { toast } from 'react-toastify';
import { useApp } from '../context/AppContext';
import './Layout.css';

const Layout = ({ children }) => {
  const { user, logout, updatePreferences } = useAuth();
  const { selectedEvent, generateReport } = useApp();
  const navigate = useNavigate();
  const location = useLocation();
  const [lowDataMode, setLowDataMode] = useState(user?.preferences?.lowDataMode || false);

  const handleLogout = () => {
    logout();
    toast.success('Logged out successfully');
    navigate('/login');
  };

  const handleLowDataModeToggle = async () => {
    const newValue = !lowDataMode;
    setLowDataMode(newValue);
    await updatePreferences({ lowDataMode: newValue });
    if (newValue) {
      toast.info('Low Data Mode enabled — heavy API calls disabled');
    } else {
      toast.info('Low Data Mode disabled — full data loading restored');
    }
  };

  const handleGenerateReport = async () => {
    if (!selectedEvent) {
      toast.error('Please select an event first');
      return;
    }
    const result = await generateReport(selectedEvent._id);
    if (result.success) {
      toast.success('Report generated successfully');
    } else {
      toast.error(result.message);
    }
  };

  const getRoleIcon = (role) => {
    switch (role) {
      case 'administrator':
        return '👑';
      case 'loss_adjuster':
        return '🔍';
      case 'viewer':
        return '👁️';
      default:
        return '👤';
    }
  };

  const getRoleLabel = (role) => {
    switch (role) {
      case 'administrator':
        return 'Administrator';
      case 'loss_adjuster':
        return 'Loss Adjuster';
      case 'viewer':
        return 'Viewer';
      default:
        return role;
    }
  };

  const isAdminPage = location.pathname.startsWith('/admin');
  const isFloodsPage = location.pathname === '/floods';
  const isPropertyPage = location.pathname === '/property';
  const isFmdPage = location.pathname === '/fmd';

  return (
    <div className="layout">
      {/* Header */}
      <header className="header">
        <div className="header-left">
          <div className="logo" onClick={() => navigate('/')} style={{ cursor: 'pointer' }}>
            <img src="/logo.png" alt="Geoventory Logo" className="logo-image" />
            <div className="logo-text">
              <h1>Geoventory</h1>
              <span>Earth Observation Climate Resilience Platform</span>
            </div>
          </div>
        </div>

        <div className="header-center">
          <nav className="main-nav">
            <button
              className={`nav-btn ${!isAdminPage && !isFloodsPage && !isPropertyPage && !isFmdPage ? 'active' : ''}`}
              onClick={() => navigate('/')}
            >
              <FiHome />
              Dashboard
            </button>
            <button
              className={`nav-btn ${isFloodsPage ? 'active' : ''}`}
              onClick={() => navigate('/floods')}
            >
              <FiDroplet />
              Floods
            </button>
            <button
              className={`nav-btn ${isPropertyPage ? 'active' : ''}`}
              onClick={() => navigate('/property')}
            >
              <FiMapPin />
              Property
            </button>
            <button
              className={`nav-btn ${isFmdPage ? 'active' : ''}`}
              onClick={() => navigate('/fmd')}
            >
              <FiActivity />
              FMD Risk
            </button>
            {user?.role === 'administrator' && (
              <button 
                className={`nav-btn ${isAdminPage ? 'active' : ''}`}
                onClick={() => navigate('/admin/users')}
              >
                <FiUsers />
                Manage Users
              </button>
            )}
          </nav>
        </div>

        <div className="header-right">
          <div className="user-info">
            <div className="user-role-badge">
              <span className="role-icon">{getRoleIcon(user?.role)}</span>
              <span className="role-label">{getRoleLabel(user?.role)}</span>
            </div>
            <span className="user-name">{user?.name}</span>
          </div>
        </div>
      </header>

      {/* Secondary Header */}
      <div className="secondary-header">
        <div className="toggle-controls">
          <button 
            className={`toggle-btn ${lowDataMode ? 'active' : ''}`}
            onClick={handleLowDataModeToggle}
            title="Reduces API calls by skipping GEE, air quality, and space hazard data"
          >
            {lowDataMode ? <FiToggleRight /> : <FiToggleLeft />}
            Low Data Mode {lowDataMode && '⚡'}
          </button>
        </div>

        <div className="header-actions">
          {!isAdminPage && (
            <button className="btn btn-primary" onClick={handleGenerateReport}>
              <FiFileText />
              Generate PDF Report
            </button>
          )}
          <button className="btn btn-outline" onClick={handleLogout}>
            <FiLogOut />
            Logout
          </button>
        </div>
      </div>

      {/* Main Content */}
      <main className="main-content">
        {children}
      </main>

      {/* Footer */}
      <footer className="footer">
        <div className="footer-content">
          <div className="footer-powered">
            <span></span>
          </div>
          <div className="footer-copyright">
            <span>© {new Date().getFullYear()} Geoventory. All Rights Reserved.</span>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Layout;
